<template>
  <div class="success-page">
    <div class="success-card">
      <!-- Check icon -->
      <div class="check-circle">
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </div>

      <h1 class="heading">{{ heading }}</h1>
      <p class="welcome">Welcome to <strong>Lulu Rewards</strong></p>
      <p class="body-text">
        Your business rewards programme has been successfully set up.<br />
        Download the Lulu Rewards app to start scanning and rewarding your customers.
      </p>

      <!-- Setup-pending status -->
      <p v-if="setupPending" class="setup-pending" role="status">
        <v-progress-circular indeterminate color="primary" :size="16" :width="2" class="setup-pending-spinner" />
        {{ setupStatusText }}
      </p>

      <!-- Go to dashboard CTA -->
      <button
        type="button"
        class="dashboard-btn"
        :disabled="dashboardDisabled"
        :aria-busy="goingToDashboard || setupPending"
        @click="goToDashboard"
      >
        <span v-if="goingToDashboard" class="dashboard-btn-spinner" aria-hidden="true">
          <v-progress-circular indeterminate color="white" :size="16" :width="2" />
        </span>
        {{ buttonLabel }}
      </button>

      <button
        v-if="setupTimedOut"
        type="button"
        class="manual-retry-btn"
        @click="manualRetry"
      >
        Retry now
      </button>

      <!-- App Store badge -->
      <a
        href="https://apps.apple.com/us/app/lulu-rewards/id6744280007?platform=iphone"
        target="_blank"
        rel="noopener noreferrer"
        class="appstore-link"
        aria-label="Download on the App Store"
      >
        <img
          src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
          alt="Download on the App Store"
          class="appstore-badge"
        />
      </a>

      <p class="footer-text">
        Questions? Reach us at
        <a href="mailto:hello@lulurewards.co.uk" class="email-link">hello@lulurewards.co.uk</a>
      </p>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { track } from '@/analytics.js';
import { authStore, refreshEmployers } from '@/stores/authStore.js';

// Exponential-backoff schedule (ms). Total ~15s before we ease off.
const POLL_SCHEDULE_MS = [1000, 2000, 4000, 8000];

export default {
  name: 'SignUpSuccessComponent',
  setup() {
    const router = useRouter();
    const goingToDashboard = ref(false);
    const setupPending = ref(true);
    const setupTimedOut = ref(false);
    const attempt = ref(0);

    let timer = null;
    let cancelled = false;

    const hasEmployers = () => {
      const ids = authStore.employerBusinessIds;
      return Array.isArray(ids) && ids.length > 0;
    };

    const stopPolling = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const schedule = (delay) => {
      stopPolling();
      timer = setTimeout(runPoll, delay);
    };

    const runPoll = async () => {
      if (cancelled) return;
      try {
        await refreshEmployers();
      } catch {
        /* swallow - guard will recover on click-through */
      }
      if (cancelled) return;
      if (hasEmployers()) {
        setupPending.value = false;
        setupTimedOut.value = false;
        track("business_post_signup_ready", {});
        return;
      }
      const next = POLL_SCHEDULE_MS[attempt.value];
      attempt.value += 1;
      if (next != null) {
        schedule(next);
      } else {
        setupPending.value = false;
        setupTimedOut.value = true;
        track("business_post_signup_pending_timeout", {});
      }
    };

    const manualRetry = () => {
      attempt.value = 0;
      setupTimedOut.value = false;
      setupPending.value = true;
      schedule(0);
    };

    onMounted(() => {
      track("businessOnboarding_paid", {});
      // Kick off the first refresh immediately, then back off.
      schedule(0);
    });

    onBeforeUnmount(() => {
      cancelled = true;
      stopPolling();
    });

    const goToDashboard = async () => {
      if (goingToDashboard.value || dashboardDisabled.value) return;
      goingToDashboard.value = true;
      track("business_post_signup_dashboard_clicked", {});
      try {
        await refreshEmployers();
      } catch {
        /* guard will recover */
      }
      router.push("/dashboard/insights");
    };

    const dashboardDisabled = computed(
      () => goingToDashboard.value || (setupPending.value && !hasEmployers())
    );

    const heading = computed(() =>
      hasEmployers() ? "You're all set!" : "Finalising your setup…"
    );

    const buttonLabel = computed(() => {
      if (goingToDashboard.value) return "Loading dashboard…";
      if (hasEmployers()) return "Your dashboard is ready";
      if (setupTimedOut.value) return "Go to dashboard";
      return "Finalising your setup…";
    });

    const setupStatusText = computed(() =>
      setupTimedOut.value
        ? "This is taking longer than expected - refresh or contact support."
        : "We're confirming your subscription with Stripe. This usually takes a few seconds."
    );

    return {
      goToDashboard,
      goingToDashboard,
      dashboardDisabled,
      setupPending,
      setupTimedOut,
      setupStatusText,
      heading,
      buttonLabel,
      manualRetry,
    };
  },
};
</script>

<style scoped>
.success-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--lulu-bg-gradient);
  padding: 24px;
}

.success-card {
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 48px 40px 40px;
  max-width: 480px;
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.lulu-logo {
  height: 56px;
  object-fit: contain;
  margin-bottom: 28px;
}

.check-circle {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--lulu-cta-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--lulu-card-shadow-hover);
  margin-bottom: 24px;
}

.heading {
  font-size: 28px;
  font-weight: 800;
  color: var(--lulu-text-strong);
  margin: 0 0 8px;
  letter-spacing: -0.5px;
}

.welcome {
  font-size: 17px;
  color: var(--lulu-primary);
  font-weight: 600;
  margin: 0 0 16px;
}

.body-text {
  font-size: 14px;
  color: var(--lulu-text-muted);
  line-height: 1.7;
  margin: 0 0 24px;
}

.dashboard-btn {
  background: rgb(0, 109, 62);
  color: #ffffff;
  border: none;
  font-weight: 600;
  font-size: 14px;
  padding: 8px 22px;
  border-radius: 999px;
  cursor: pointer;
  text-transform: none;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 20px;
}

.dashboard-btn:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3);
  transform: translateY(-1px);
}

.dashboard-btn:disabled {
  opacity: 0.65;
  cursor: default;
  transform: none;
}

.setup-pending {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 18px;
  padding: 8px 14px;
  border-radius: var(--lulu-radius-input);
  background: var(--lulu-primary-soft);
  color: var(--lulu-primary-dark);
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--lulu-primary-border);
  max-width: 360px;
  line-height: 1.4;
  text-align: left;
}

.setup-pending-spinner {
  flex-shrink: 0;
}

.manual-retry-btn {
  background: transparent;
  color: var(--lulu-primary);
  border: 1px solid var(--lulu-primary-border);
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.manual-retry-btn:hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3);
  transform: translateY(-1px);
}

.dashboard-btn-spinner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-right: 8px;
  vertical-align: middle;
}

.appstore-link {
  display: inline-block;
  margin-bottom: 28px;
  transition: transform 0.2s, opacity 0.2s;
}

.appstore-link:hover {
  transform: translateY(-2px);
  opacity: 0.88;
}

.appstore-badge {
  height: 48px;
}

.footer-text {
  font-size: 12px;
  color: var(--lulu-text-subtle);
  margin: 0;
}

.email-link {
  color: var(--lulu-link);
  text-decoration: none;
  font-weight: 500;
}

.email-link:hover {
  text-decoration: underline;
}
</style>
